import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  resolveFieldShortcutFromConfig,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@folio/components';
import { useCustomFieldConfigs } from '@folio/components/module-elements/InformationCard/useCustomFieldConfigs';
import {
  Asset,
  Customer,
  InformationFieldParams,
  InformationSeparatorParams,
  Policy,
} from 'korint-sdk';
import { ReactNode } from 'react';
import { defaultFormatValue } from './InformationCard/InformationField';
import { useTranslation } from 'react-i18next';

type CompareCardProps = {
  title: ReactNode;
  productId: string;
  fieldsConfig: Array<string | InformationFieldParams | InformationSeparatorParams>;
  entityType: 'customer' | 'policy' | 'asset';
  entities: (Policy | undefined)[] | (Customer | undefined)[] | (Asset | undefined)[];
  isLoading?: boolean;
};

export function CompareCard({
  title,
  productId,
  fieldsConfig,
  entityType,
  entities,
  isLoading,
}: CompareCardProps) {
  const { t } = useTranslation();

  const customFieldConfigs = useCustomFieldConfigs({ productId, entityType });
  const staticKeys = new Set(
    fieldsConfig.map(field => (typeof field === 'string' ? field : 'key' in field && field.key)),
  );
  const dynamicKeys = customFieldConfigs
    .filter(({ key, display }) => display?.step && !staticKeys.has(key))
    .map(({ key }) => key);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table className="[&_tr]:border-0 table-fixed">
          <TableHeader className="[&_tr]:border-0">
            <TableRow>
              <TableHead />
              {entities.map((_, index) => (
                <TableHead key={index} className="">
                  {index === 0 && entities.length > 1
                    ? t('compareTable.previousInformation')
                    : t('compareTable.newInformation')}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...fieldsConfig, ...dynamicKeys].map(shortcut => {
              if (typeof shortcut === 'object' && shortcut.type === 'separator') {
                return null;
              }
              const fieldConfig = resolveFieldShortcutFromConfig(
                shortcut as string | InformationFieldParams,
                customFieldConfigs,
              );
              if (!fieldConfig) {
                return null;
              }

              const validValues = entities
                .map(result => result?.customFields[fieldConfig.key])
                .filter(value => value !== null && value !== undefined);

              if (fieldConfig.hideIfEmpty && validValues.length === 0) {
                return null;
              }

              const hasChanges =
                validValues.length > 1 && !validValues.every(value => value === validValues[0]);

              const customFieldConfig = customFieldConfigs.find(
                ({ key }) => key === fieldConfig.key,
              );
              const display = customFieldConfig?.display;
              const columnKeys = customFieldConfig?.columns?.map(({ key }) => key) ?? [];
              const optionLabels = display?.optionLabels as Record<string, string> | undefined;
              const formatValue = fieldConfig.formatValue ?? defaultFormatValue[fieldConfig.type];

              return (
                <TableRow key={fieldConfig.key} className={hasChanges ? 'bg-slate-100' : ''}>
                  <TableCell className="font-medium">
                    {display?.label ??
                      t([
                        `fields.${fieldConfig.key}.informationField.label`,
                        `fields.${fieldConfig.key}.label`,
                      ])}
                  </TableCell>
                  {entities.map((entity, index) => {
                    const value = entity?.customFields[fieldConfig.key];
                    // A bit of "magic" here to handle the backend ENUM fields that don't have a match in the frontend for now
                    // Should be removed for something more explicit when harmonizing field types accross the platform
                    const valueTranslation = t([
                      `fields.${fieldConfig.key}.options.${value}`,
                      `options.${value}`,
                      'empty',
                    ]);
                    const formattedValue = Array.isArray(value)
                      ? value
                          .map(row => columnKeys.map(columnKey => row[columnKey] ?? '').join(' / '))
                          .join(', ')
                      : optionLabels?.[String(value)]
                        ? optionLabels[String(value)]
                        : valueTranslation
                          ? valueTranslation
                          : formatValue
                            ? formatValue(value)
                            : value;

                    return (
                      <TableCell key={index} className="text-sm overflow-hidden text-ellipsis">
                        {isLoading ? (
                          <Skeleton className="w-full h-4" />
                        ) : value !== undefined ? (
                          formattedValue
                        ) : (
                          <div className="italic text-muted-foreground">-</div>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
